import { defineStore } from 'pinia';

function getInitial(localKey, hardcoded) {
    return localStorage.getItem(localKey) || hardcoded;
}

export const useThemeStore = defineStore('theme', {
    state: () => ({
        mode: localStorage.getItem('theme_mode') || 'light',
        
        colors: {
            primary: getInitial('theme-primary', '#fcd535'),
            lightBg: getInitial('theme-light-bg', '#ffffff'),
            darkBg: getInitial('theme-dark-bg', '#181A20'),
            success: getInitial('theme-success', '#0ECB81'),
            error: getInitial('theme-error', '#F6465D'),
            warning: getInitial('theme-warning', '#F0B90B'),
            info: getInitial('theme-info', '#1E9FF2'),
        },
        
        serverDefaults: null,

        devMode: import.meta.env.DEV || localStorage.getItem('theme-dev-mode') === 'true' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('theme')),
    }),
    
    getters: {
        isDark: (state) => state.mode === 'dark',
        isLight: (state) => state.mode === 'light',
        currentBg: (state) => state.mode === 'dark' ? state.colors.darkBg : state.colors.lightBg,
        getPrimary: (state) => state.colors.primary,
        getLightBg: (state) => state.colors.lightBg,
        getDarkBg: (state) => state.colors.darkBg,
        getSuccess: (state) => state.colors.success,
        getError: (state) => state.colors.error,
        getWarning: (state) => state.colors.warning,
        getInfo: (state) => state.colors.info,
        
        // Get all CSS variables as an object for inline styles
        cssVariables: (state) => {
            const isDark = state.mode === 'dark';
            const bgColor = isDark ? state.colors.darkBg : state.colors.lightBg;
            
            // Text colors (WCAG contrast: tertiary/secondary darker)
            const textColor = isDark ? '#EAECEF' : '#1E2329';
            const textSecondary = isDark ? '#848E9C' : '#4b5563';
            const textTertiary = isDark ? '#6b7280' : '#6b7280';
            
            // Derive surface colors from background
            let surfaceColor, surfaceColorAlt, cardColor, inputBg, borderColor, borderColorLight, hoverBg;
            
            if (isDark) {
                surfaceColor = adjustBrightness(state.colors.darkBg, 10);
                surfaceColorAlt = adjustBrightness(state.colors.darkBg, 20);
                cardColor = adjustBrightness(state.colors.darkBg, 15);
                inputBg = adjustBrightness(state.colors.darkBg, 30);
                borderColor = adjustBrightness(state.colors.darkBg, 40);
                borderColorLight = adjustBrightness(state.colors.darkBg, 60);
                hoverBg = adjustBrightness(state.colors.darkBg, 25);
            } else {
                surfaceColor = adjustBrightness(state.colors.lightBg, -3);
                surfaceColorAlt = adjustBrightness(state.colors.lightBg, -5);
                cardColor = state.colors.lightBg;
                inputBg = adjustBrightness(state.colors.lightBg, -3);
                borderColor = adjustBrightness(state.colors.lightBg, -10);
                borderColorLight = adjustBrightness(state.colors.lightBg, -5);
                hoverBg = adjustBrightness(state.colors.lightBg, -5);
            }
            
            return {
                // Primary colors
                '--theme-primary': state.colors.primary,
                '--theme-primary-hover': adjustBrightness(state.colors.primary, -10),
                '--theme-primary-light': state.colors.primary + '1A',
                '--theme-primary-alpha': state.colors.primary + '1A',
                
                // Background colors
                '--theme-bg': bgColor,
                '--theme-light-bg': state.colors.lightBg,
                '--theme-dark-bg': state.colors.darkBg,
                '--theme-bg-card': cardColor,
                '--theme-bg-hover': hoverBg,
                
                // Surface colors
                '--theme-surface': surfaceColor,
                '--theme-surface-alt': surfaceColorAlt,
                '--theme-card': cardColor,
                '--theme-input': inputBg,
                
                // Text colors
                '--theme-text': textColor,
                '--theme-text-primary': textColor,
                '--theme-text-secondary': textSecondary,
                '--theme-text-tertiary': textTertiary,
                '--theme-input-placeholder': isDark ? 'rgba(94, 102, 115, 0.44)' : 'rgba(197, 197, 197, 0.54)',
                '--theme-text-on-primary': getContrastColor(state.colors.primary),
                
                // Border colors
                '--theme-border': borderColor,
                '--theme-border-light': borderColorLight,
                
                // Status colors - Success
                '--theme-success': state.colors.success,
                '--theme-success-hover': adjustBrightness(state.colors.success, -10),
                '--theme-success-alpha': state.colors.success + '1A',
                '--theme-buy': state.colors.success,
                '--theme-positive': state.colors.success,
                
                // Status colors - Error/Danger
                '--theme-error': state.colors.error,
                '--theme-error-hover': adjustBrightness(state.colors.error, -10),
                '--theme-error-alpha': state.colors.error + '1A',
                '--theme-danger': state.colors.error, // Alias for error
                '--theme-danger-hover': adjustBrightness(state.colors.error, -10),
                '--theme-danger-alpha': state.colors.error + '1A',
                '--theme-sell': state.colors.error,
                '--theme-negative': state.colors.error,
                
                // Status colors - Warning
                '--theme-warning': state.colors.warning,
                '--theme-warning-alpha': state.colors.warning + '1A',
                
                // Status colors - Info
                '--theme-info': state.colors.info,
                '--theme-info-alpha': state.colors.info + '1A',
                '--theme-link': state.colors.info,
                
                // Shadow
                '--theme-shadow': isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)',
                
                // Overlay
                '--theme-overlay': 'rgba(0,0,0,0.5)',
            };
        }
    },
    
    actions: {
        // Toggle between light and dark mode
        toggleMode() {
            this.mode = this.mode === 'light' ? 'dark' : 'light';
            this.persistMode();
            this.applyTheme();
        },
        
        // Set specific mode
        setMode(mode) {
            this.mode = mode;
            this.persistMode();
            this.applyTheme();
        },
        
        // Update primary/accent color
        setPrimaryColor(color) {
            this.colors.primary = color;
            localStorage.setItem('theme-primary', color);
            this.applyTheme();
        },
        
        // Update light background color
        setLightBgColor(color) {
            this.colors.lightBg = color;
            localStorage.setItem('theme-light-bg', color);
            this.applyTheme();
        },
        
        // Update dark background color
        setDarkBgColor(color) {
            this.colors.darkBg = color;
            localStorage.setItem('theme-dark-bg', color);
            this.applyTheme();
        },
        
        // Update success/buy color
        setSuccessColor(color) {
            this.colors.success = color;
            localStorage.setItem('theme-success', color);
            this.applyTheme();
        },
        
        // Update error/sell color
        setErrorColor(color) {
            this.colors.error = color;
            localStorage.setItem('theme-error', color);
            this.applyTheme();
        },
        
        // Update warning color
        setWarningColor(color) {
            this.colors.warning = color;
            localStorage.setItem('theme-warning', color);
            this.applyTheme();
        },
        
        // Update info color
        setInfoColor(color) {
            this.colors.info = color;
            localStorage.setItem('theme-info', color);
            this.applyTheme();
        },
        
        resetColors() {
            const d = this.serverDefaults || {};
            this.colors.primary = d.primary || '#fcd535';
            this.colors.lightBg = d.light_bg || '#ffffff';
            this.colors.darkBg = d.dark_bg || '#181A20';
            this.colors.success = d.success || '#0ECB81';
            this.colors.error = d.error || '#F6465D';
            this.colors.warning = d.warning || '#F0B90B';
            this.colors.info = d.info || '#1E9FF2';
            
            localStorage.removeItem('theme-primary');
            localStorage.removeItem('theme-light-bg');
            localStorage.removeItem('theme-dark-bg');
            localStorage.removeItem('theme-success');
            localStorage.removeItem('theme-error');
            localStorage.removeItem('theme-warning');
            localStorage.removeItem('theme-info');
            
            this.applyTheme();
        },
        
        // Persist mode to localStorage (use consistent key)
        persistMode() {
            localStorage.setItem('theme_mode', this.mode);
            // Also set the old key for backward compatibility
            localStorage.setItem('theme-mode', this.mode);
        },
        
        // Apply theme to document
        applyTheme() {
            const root = document.documentElement;
            const cssVars = this.cssVariables;
            
            // Set all CSS variables on :root
            Object.keys(cssVars).forEach(key => {
                root.style.setProperty(key, cssVars[key]);
            });
            
            // Add/remove dark class on html element
            if (this.mode === 'dark') {
                document.documentElement.classList.add('dark');
                document.documentElement.setAttribute('data-theme', 'dark');
                document.body.classList.add('dark-mode');
                document.body.classList.add('dark');
                document.body.classList.remove('light-mode');
            } else {
                document.documentElement.classList.remove('dark');
                document.documentElement.setAttribute('data-theme', 'light');
                document.body.classList.remove('dark-mode');
                document.body.classList.remove('dark');
                document.body.classList.add('light-mode');
            }
        },
        
        setServerDefaults(themeColors) {
            if (!themeColors) return;
            this.serverDefaults = themeColors;
            const colorMap = { primary: 'primary', light_bg: 'lightBg', dark_bg: 'darkBg', success: 'success', error: 'error', warning: 'warning', info: 'info' };
            const localKeys = { primary: 'theme-primary', lightBg: 'theme-light-bg', darkBg: 'theme-dark-bg', success: 'theme-success', error: 'theme-error', warning: 'theme-warning', info: 'theme-info' };
            for (const [serverKey, stateKey] of Object.entries(colorMap)) {
                if (!localStorage.getItem(localKeys[stateKey]) && themeColors[serverKey]) {
                    this.colors[stateKey] = themeColors[serverKey];
                }
            }
        },

        initTheme(initialMode = null, themeColors = null) {
            if (themeColors) {
                this.setServerDefaults(themeColors);
            }

            if (initialMode) {
                this.mode = initialMode;
            } else {
                const savedMode = localStorage.getItem('theme_mode') || localStorage.getItem('theme-mode');
                if (savedMode) {
                    this.mode = savedMode;
                }
            }
            
            const savedPrimary = localStorage.getItem('theme-primary');
            const savedLightBg = localStorage.getItem('theme-light-bg');
            const savedDarkBg = localStorage.getItem('theme-dark-bg');
            const savedSuccess = localStorage.getItem('theme-success');
            const savedError = localStorage.getItem('theme-error');
            const savedWarning = localStorage.getItem('theme-warning');
            const savedInfo = localStorage.getItem('theme-info');
            
            if (savedPrimary) this.colors.primary = savedPrimary;
            if (savedLightBg) this.colors.lightBg = savedLightBg;
            if (savedDarkBg) this.colors.darkBg = savedDarkBg;
            if (savedSuccess) this.colors.success = savedSuccess;
            if (savedError) this.colors.error = savedError;
            if (savedWarning) this.colors.warning = savedWarning;
            if (savedInfo) this.colors.info = savedInfo;
            
            this.applyTheme();
        },
        
        // Toggle dev mode for theme customizer
        toggleDevMode() {
            this.devMode = !this.devMode;
            localStorage.setItem('theme-dev-mode', this.devMode ? 'true' : 'false');
        },
        
        // Enable dev mode
        enableDevMode() {
            this.devMode = true;
            localStorage.setItem('theme-dev-mode', 'true');
        },
    }
});

// Helper function to adjust color brightness
function adjustBrightness(hex, percent) {
    if (!hex) return '#000000';
    
    // Remove # if present
    hex = hex.replace(/^#/, '');
    
    // Handle short hex codes
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    // Parse hex to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Validate parsed values
    if (isNaN(r)) r = 0;
    if (isNaN(g)) g = 0;
    if (isNaN(b)) b = 0;
    
    // Adjust brightness
    r = Math.max(0, Math.min(255, r + (r * percent / 100)));
    g = Math.max(0, Math.min(255, g + (g * percent / 100)));
    b = Math.max(0, Math.min(255, b + (b * percent / 100)));
    
    // Convert back to hex
    const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Get contrasting text color for a background
function getContrastColor(hexColor) {
    if (!hexColor) return '#FFFFFF';
    
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#1E2329' : '#FFFFFF';
}
